/**
 * Created by piotr.pozniak@thebeaverhead.com on 24/10/2018.
 */

import React, { useCallback, useEffect, useState } from "react";

import { Link } from "react-router-dom";
import SidebarTab from "./SidebarTab";
import LoadingIndicator from "../../../calendar/components/LoadingIndicator";
import SuccessAlert from "../SuccessAlert";
import ApiError from "../ApiError";
import {
  canPublishWidgetWithinQuota,
  getTheLowestPlanMeetingCalendarCriteria,
  hasRemainingWidgetQuota,
} from "../../tools";
import { useCurrentOrganizationStore } from "../../../../hooks/redux/currentOrganization";
import Accordion from "./Acordion";
import RadioInput from "./RadioInput";
import ShareableUrl from "./ShareableUrl";
import classNames from "classnames";
import WidgetInstallModal from "../../modals/WidgetInstallModal";
import QRCode from "./QRCode";
import GraceThreshold from "../Subscription/GraceThreshold";

/**
 *
 * @param {string} widgetTypeName
 * @param {Object} props
 * @param {Object} props.widgetModel - The widget model object.
 * @param {Object} props.widget - The widget object for event filters.
 * @param {Object} props.errors - The errors object containing form errors.
 * @param {Object} props.currentOrganization - The current organization object.
 * @param {Function} props.onUpdateStatus - Function to update the widget status.
 * @param {Function} props.onUpdateField - Function to update a field in the form.
 * @param {Function} props.onUpdateWidgetSettings - Function to update widget settings.
 * @param {Function} props.onSubmit - Function to call on form submission.
 * @param {boolean} props.isNew - Whether the widget is new.
 * @param {boolean} props.hasDomains - Whether the widget has domains.
 * @param {boolean} props.disabled - Whether the form is disabled.
 * @param {string} props.shareableUrl - The shareable URL for the widget.
 * @param {string} props.widgetType - The type of the widget (e.g., "calendar", "groups").
 * @param {string} props.widgetTypeName - Printable name of the widget type (e.g., "Calendar Widget").
 *
 *
 * @returns {Element}
 * @constructor
 */
const InstallTab = ({ widgetTypeName = "Widget", ...props }) => {
  const { widgetModel, widget } = props;
  const widgetType = widget.model.widget_type;
  const { currentOrganization } = useCurrentOrganizationStore();
  const [showInstallModal, setShowInstallModal] = useState(false);

  useEffect(() => {
    if (widget.updateSuccess) {
      window.updateJquery();
    }
  }, [widget.updateSuccess]);

  const canPublishWidget = canPublishWidgetWithinQuota(
    currentOrganization,
    Boolean(widget.model.available)
  );
  const isPublishDisabled =
    widget.update || (!widget.model.available && !canPublishWidget)
      ? true
      : false;

  const recommendedPlan = !canPublishWidget
    ? getTheLowestPlanMeetingCalendarCriteria(currentOrganization)
    : null;

  const publishRestrictionWarning =
    !widgetModel.available && !canPublishWidget ? (
      <div className={"alert alert-danger"}>
        <div className={"d-flex align-items-start"}>
          <i className={"material-icons icon-sm text-danger p-0 m-0 m-r-5"}>
            warning
          </i>
          <div>
            <p>
              You cannot publish this {widgetType} widget because you have
              reached the maximum number of widgets allowed for your current
              plan.
            </p>
            {recommendedPlan && (
              <p>
                Click here to upgrade to the minimum required plan{" "}
                <strong>{recommendedPlan}</strong>.
              </p>
            )}
            <div
              className={
                "d-flex flex-column justify-content-center align-items-center"
              }
            >
              <Link to={"/billing"}>
                <button className={"btn btn-success btn-sm"}>
                  Upgrade now
                </button>
              </Link>
              <GraceThreshold />
            </div>
          </div>
        </div>
      </div>
    ) : null;

  /**
   *
   * @type {(function(*): void)|*}
   */
  const onChangeStatus = useCallback(
    (field) => (e) => {
      props.onUpdateStatus(!(widget.model.available * 1) ? 1 : 0);
    },
    [props.onUpdateStatus, widget]
  );

  /**
   *
   * @type {function(*): function(*): void}
   */
  const onChangeStatusButton = useCallback(
    (e) => {
      onChangeStatus("available")(e);
    },
    [onChangeStatus]
  );

  const onShowInstallModal = useCallback(() => {
    setShowInstallModal(true);
  }, [setShowInstallModal]);

  /**
   *
   * @type {(function(): void)|*}
   */
  const onCloseInstallModal = useCallback(() => {
    setShowInstallModal(false);
  }, [setShowInstallModal]);

  const widgetTypeNamePrintable = widgetTypeName.toLowerCase();

  const installButton =
    widgetModel.available && canPublishWidget ? (
      <div className={"d-flex flex-column"}>
        <div className={"my-2"}>
          <button
            className={classNames("btn btn-primary w-100", {
              disabled: props.disabled || !widgetModel.available,
            })}
            disabled={props.disabled || !widgetModel.available}
            onClick={onShowInstallModal}
          >
            Install
          </button>
        </div>
      </div>
    ) : null;

  const publishRadio =
    canPublishWidget || widgetModel.available ? (
      <RadioInput
        fieldName={"available"}
        label={"Make this widget available to others"}
        onChange={onChangeStatus}
        checked={widgetModel.available}
        disabled={isPublishDisabled}
        tooltip={
          widget.model.available * 1 === 0
            ? {
                message: `In order to share your ${widgetTypeNamePrintable} and make it accessible by
              others, change it status to published. You can make it here using
              the toggle button.`,
              }
            : null
        }
      />
    ) : null;

  const publishCard = (
    <Accordion
      title={"Publish"}
      subtitle={`Your ${widgetTypeNamePrintable} is ${
        widget.model.available * 1 == 0 ? " not" : ""
      } published.`}
      keepExpanded={true}
    >
      {publishRadio}
      {publishRestrictionWarning}
      {installButton}

      <SuccessAlert className={"p-2"}>
        {widget.updateSuccess ? "Changes have been saved!" : null}
      </SuccessAlert>
    </Accordion>
  );

  const unpublishedWarning =
    !widgetModel.available && canPublishWidget ? (
      <div
        className={
          "alert alert-warning fade show d-flex align-items-center flex-column p-2"
        }
      >
        This widget is unpublished. Publish your widget first.
        <button
          className="btn btn-primary btn-sm m-2"
          onClick={onChangeStatusButton}
        >
          Publish
        </button>
      </div>
    ) : null;

  const installModal = showInstallModal ? (
    <WidgetInstallModal
      widgetType={widgetType}
      uuid={widget.model.uuid}
      slug={widget.model.slug}
      show={showInstallModal}
      onClose={onCloseInstallModal}
      widgetTypePrintable={widgetTypeNamePrintable}
      widgetTemplate={widget.model.template}
    />
  ) : null;

  const footer = !props.disabled ? (
    <div>
      <ApiError
        error={widget.updateError}
        defaultErrorMessage={"Could not save your settings. Please try again"}
      />
    </div>
  ) : (
    <LoadingIndicator />
  );

  return (
    <SidebarTab id="install_tab" show={false} footer={footer}>
      {publishCard}

      {widget.model.available && props.shareableUrl ? (
        <>
          <Accordion
            title={"Hosted Link and Redirection"}
            subtitle={
              "Copy the widget-hosted Link and/or set the destination for navigation."
            }
            defaultExpanded={widget.model.available}
          >
            <ShareableUrl
              url={props.shareableUrl}
              widgetModel={widgetModel}
              widgetTypeName={widgetTypeNamePrintable}
              onUpdateSettings={props.onUpdateWidgetSettings}
              onSubmit={props.onSubmit}
            />
          </Accordion>
          <Accordion
            title={`QR code`}
            subtitle={
              "Download your QR Code. Utilize it in your posters and social media promotions to enhance visibility."
            }
          >
            <QRCode
              url={props.shareableUrl}
              title={widgetModel.name}
              subtitle={`QR Code for '${widgetModel.name}' ${widgetTypeNamePrintable}`}
            />
          </Accordion>
        </>
      ) : null}

      <div className={"mx-2"}>{unpublishedWarning}</div>
      {installModal}
    </SidebarTab>
  );
};

export default InstallTab;
